import { SHARED_ACTIONS } from 'constants/Actions/Shared';
import { USERS_ACTIONS } from 'constants/Actions/Users';
import { CONSTANTS_MESSAGE } from 'constants/ConstantsMessages';
import { LANGUAGES } from 'constants/Languages';
import { Login, Logout, CheckToken, CheckUsername } from 'redux/services/LoginServices';
import { setEmptyTokenLocalStorange, setTokenLocalStorange } from 'redux/services/StorageServices';
import { Add, ChangePassword, Delete, Find, Get, Update } from 'redux/services/UserServices';
import { actionActiveLoading, actionDesactiveLoading, actionDispatch, actionManageMessage, actionMessage } from './SharedActions';
import { actionChangeLanguage } from './TranslatorActions';

/**
 * Funcion que corresponde a la funcionalidad login de la plataforma
 * 
 * @param {string} sUsername Nombre de usuario
 * @param {string} sPassword Password del usuario
 * 
 * @author Leandro Curbelo
 */
export const actionCheckUsername = (sUsername) => {
    return dispatch => {
        dispatch(actionActiveLoading());
        CheckUsername(sUsername).then(async oSuccess => {
            if (oSuccess.data)
                switch (oSuccess.data) {
                    case LANGUAGES.SPANISH:
                    default:
                        dispatch(actionChangeLanguage(LANGUAGES.SPANISH));
                        break;
                    case LANGUAGES.ENGLISH:
                        dispatch(actionChangeLanguage(LANGUAGES.ENGLISH));
                        break;
                    case LANGUAGES.PORTUGUESE:
                        dispatch(actionChangeLanguage(LANGUAGES.PORTUGUESE));
                        break;
                }
            else
                dispatch(actionChangeLanguage(LANGUAGES.PORTUGUESE));
            dispatch(actionDispatch(SHARED_ACTIONS.CHECK_USERNAME));
        }).catch((oError) => {
            dispatch(actionManageMessage(oError));
        }).finally(() => dispatch(actionDesactiveLoading()))
    }
}
/**
 * Funcion que corresponde a la funcionalidad login de la plataforma
 * 
 * @param {string} sEmail Email del usuario
 * @param {string} sPassword Password del usuario
 * 
 * @author Leandro Curbelo
 */
export const actionLogin = (sEmail, sPassword) => {
    return dispatch => {
        dispatch(actionActiveLoading());
        Login(sEmail, sPassword).then(async oSuccess => {
            if (oSuccess.data && oSuccess.data.language)
                switch (oSuccess.data.language) {
                    case LANGUAGES.SPANISH:
                    default:
                        dispatch(actionChangeLanguage(LANGUAGES.SPANISH));
                        break;
                    case LANGUAGES.ENGLISH:
                        dispatch(actionChangeLanguage(LANGUAGES.ENGLISH));
                        break;
                    case LANGUAGES.PORTUGUESE:
                        dispatch(actionChangeLanguage(LANGUAGES.PORTUGUESE));
                        break;
                }
            await setTokenLocalStorange(oSuccess.data.remember_token);
            dispatch(actionDispatch(SHARED_ACTIONS.LOGIN, { oUser: oSuccess.data }))
        }).catch(oError => {
            // Condición que comprueba si el mensaje de error se identifica con el de contraseña incorrecta, de ser así se le agregan los intentos restanes
            if (oError && oError.data && oError.data.data && oError.data.data.status === 1 && oError.data.message) {
                let sMessage = `${oError.data.message} ${(3 - oError.data.data.login_fails)}`;
                return dispatch(actionMessage(sMessage));
            }
            dispatch(actionManageMessage(oError));
        }).finally(() => dispatch(actionDesactiveLoading()));
    }
}
/**
 * Funcion que corresponde a la funcionalidad logout de la plataforma
 * 
 * @author Leandro Curbelo
 */
export const actionLogout = () => {
    return dispatch => {
        dispatch(actionActiveLoading());
        Logout().then(() => { }).catch(() => { }).finally(async () => {
            await setEmptyTokenLocalStorange();
            dispatch(actionDispatch(SHARED_ACTIONS.LOGOUT));
            dispatch(actionDesactiveLoading());
        });
    }
}
/**
 * Funcion que corrobora la validez del token en sesion, si el token es correcto, el API la actualiza y retorna el usuario por lo que esta
 *  funcion se convierte en un login de fondo.
 * 
 * @author Leandro Curbelo
 */
export const actionCheckToken = () => {
    return dispatch => {
        dispatch(actionActiveLoading());
        CheckToken().then(async oSuccess => {
            await setTokenLocalStorange(oSuccess.data.remember_token);
            dispatch(actionDispatch(SHARED_ACTIONS.LOGIN, { oUser: oSuccess.data }))
        }).catch(() => {
            dispatch(actionLogout());
        }).finally(() => { dispatch(actionDesactiveLoading()); });
    }
}
/**
 * Función que limpia el reducer compartido
 * 
 * @author Leandro Curbelo
 */
export const actionClean = () => {
    return dispatch => {
        dispatch(actionDispatch(SHARED_ACTIONS.CLEAN))
    }
}

/**
 * Función que obtiene los usuarios del sistema.
 * 
 * @author Leandro Curbelo
 */
export const actionGetUsers = (nPage = 0, sFilter = '', bShowLoading = false) => {
    return dispatch => {
        bShowLoading && dispatch(actionActiveLoading());
        Get(nPage, sFilter).then(oSuccess => {
            dispatch(actionDispatch(USERS_ACTIONS.GET, oSuccess));
        }).catch(() => {
            dispatch(actionDispatch(USERS_ACTIONS.GET, []));
        }).finally(() => dispatch(actionDesactiveLoading()));
    }
}
/**
 * Función que toma un usuario en especifico en base a su identificador principal.
 * 
 * @param {number} nId Identificador principal del usuario.
 * 
 * @author Leandro Curbelo
 */
export const actionFindUser = (nId) => {
    return dispatch => {
        dispatch(actionActiveLoading());
        Find(nId).then(oSuccess => {
            dispatch(actionDispatch(USERS_ACTIONS.FIND, oSuccess.data));
        }).catch(oError => {
            dispatch(actionDispatch(USERS_ACTIONS.ERROR));
            dispatch(actionManageMessage(oError));
        }).finally(() => dispatch(actionDesactiveLoading()));
    }
}
/**
 * Función que da de alta un nuevo usuario en el sistema
 * 
 * @author Leandro Curbelo
 */
export const actionSaveUser = (oData) => {
    return dispatch => {
        dispatch(actionActiveLoading());
        Add(oData).then(oSuccess => {
            dispatch(actionDispatch(USERS_ACTIONS.SUCCESS, oSuccess.data));
            dispatch(actionMessage(oSuccess.message, CONSTANTS_MESSAGE.TYPE_SUCCESS));
        }).catch(oError => {
            dispatch(actionManageMessage(oError));
        }).finally(() => dispatch(actionDesactiveLoading()));
    }
}
/**
 * Función actualiza los datos de un usuario
 * 
 * @param {number} nUserId Identificador primario del usuario
 * @param {object} oData Toda información que pueda ser editada dentro de un usuario
 * 
 * @author Leandro Curbelo
 */
export const actionUpdateUser = (nUserId, oData) => {
    return dispatch => {
        dispatch(actionActiveLoading());
        Update(nUserId, oData).then(oSuccess => {
            dispatch(actionDispatch(USERS_ACTIONS.SUCCESS));
            dispatch(actionMessage(oSuccess.message, CONSTANTS_MESSAGE.TYPE_SUCCESS));
        }).catch(oError => {
            dispatch(actionManageMessage(oError));
        }).finally(() => dispatch(actionDesactiveLoading()));
    }
}
/**
 * Función actualiza los datos de un usuario
 * 
 * @param {number} nUserId Identificador primario del usuario
 * @param {object} oData Toda información que pueda ser editada dentro de un usuario
 * 
 * @author Leandro Curbelo
 */
export const actionChangePasswordUser = (nUserId, oData) => {
    return dispatch => {
        dispatch(actionActiveLoading());
        ChangePassword(nUserId, oData).then(oSuccess => {
            dispatch(actionDispatch(USERS_ACTIONS.SUCCESS));
            dispatch(actionMessage(oSuccess.message, CONSTANTS_MESSAGE.TYPE_SUCCESS));
        }).catch(oError => {
            dispatch(actionManageMessage(oError));
        }).finally(() => dispatch(actionDesactiveLoading()));
    }
}
/**
 * Función que elimina un usuario del sistema
 * 
 * @author Leandro Curbelo
 */
export const actionDeleteUser = (nId) => {
    return dispatch => {
        dispatch(actionActiveLoading());
        Delete(nId).then(oSuccess => {
            dispatch(actionMessage(oSuccess.message, CONSTANTS_MESSAGE.TYPE_SUCCESS));
            dispatch(actionDispatch(USERS_ACTIONS.SUCCESS));
        }).catch(oError => {
            dispatch(actionManageMessage(oError));
        }).finally(() => dispatch(actionDesactiveLoading()));
    }
}
/**
 * Función que limpia el reducer de Users
 * 
 * @author Leandro Curbelo
 */
export const actionCleanUsers = () => {
    return dispatch => {
        dispatch(actionDispatch(USERS_ACTIONS.CLEAN))
    }
}